import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout/Layout';
import DocumentsPage from './pages/DocumentsPage';
import ProfilePage from './pages/ProfilePage';
import UsagePage from './pages/UsagePage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import InvoiceViewPage from './pages/InvoiceViewPage';
import MultiInvoiceViewPage from './pages/MultiInvoiceViewPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';

// Import SuperAdmin pages
import AdminDashboardPage from './pages/Admin/DashboardPage';
import OrganizationsPage from './pages/Admin/OrganizationsPage';
import OrganizationDetailsPage from './pages/Admin/OrganizationDetailsPage';
import UsersPage from './pages/Admin/UsersPage';
import QuotaPage from './pages/Admin/QuotaPage';
import BankStatementViewPage from './pages/BankStatementViewPage';
import CashbookViewPage from './pages/CashbookViewPage';
import VATBookViewPage from './pages/VATBookViewPage';
import DataPage from './pages/DataPage';

// Protected route component with role check
const ProtectedRoute: React.FC<{ element: React.ReactNode; allowedRoles?: string[] }> = ({ element, allowedRoles }) => {
  const { isAuthenticated, currentUser } = useAuth();
  
  // Show loading state while authentication is being determined
  if (isAuthenticated === undefined) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // If no roles specified or user role is in allowed roles, render the element
  if (!allowedRoles || (currentUser && allowedRoles.includes(currentUser.role))) {
    return <>{element}</>;
  }
  
  // If user doesn't have the required role, redirect based on their role
  if (currentUser?.role === 'SuperAdmin') {
    return <Navigate to="/admin/dashboard" />;
  } else {
    return <Navigate to="/documents" />;
  }
};

// Role-based redirect component
const RoleBasedRedirect: React.FC = () => {
  const { currentUser } = useAuth();
  
  if (currentUser?.role === 'SuperAdmin') {
    return <Navigate to="/admin/dashboard" />;
  } else {
    return <Navigate to="/documents" />;
  }
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/forgot-password" element={isAuthenticated ? <Navigate to="/" /> : <ForgotPasswordPage />} />
      
      {/* Default redirect - SuperAdmin goes to admin dashboard, others to documents */}
      <Route path="/" element={
        <ProtectedRoute element={<RoleBasedRedirect />} />
      } />
      
      {/* OrgAdmin routes */}

      <Route path="/documents" element={
        <ProtectedRoute element={
          <Layout>
            <DocumentsPage />
          </Layout>
        } />
      } />

      <Route path="/data" element={
        <ProtectedRoute element={
          <Layout>
            <DataPage />
          </Layout>
        } />
      } />



      <Route path="/usage" element={
        <ProtectedRoute element={
          <Layout>
            <UsagePage />
          </Layout>
        } />
      } />
      <Route path="/invoice/:id" element={
        <ProtectedRoute element={
          <Layout>
            <InvoiceViewPage />
          </Layout>
        } />
      } />
      <Route path="/invoices" element={
        <ProtectedRoute element={
          <Layout>
            <MultiInvoiceViewPage />
          </Layout>
        } />
      } />
      <Route path="/bank-statement/:id" element={
        <ProtectedRoute element={
          <Layout>
            <BankStatementViewPage />
          </Layout>
        } />
      } />
      <Route path="/cashbook/:id" element={
        <ProtectedRoute element={
          <Layout>
            <CashbookViewPage />
          </Layout>
        } />
      } />
      <Route path="/vatbook/:id" element={
        <ProtectedRoute element={
          <Layout>
            <VATBookViewPage />
          </Layout>
        } />
      } />
      <Route path="/profile" element={
        <ProtectedRoute element={
          <Layout>
            <ProfilePage />
          </Layout>
        } />
      } />
      
      {/* SuperAdmin routes */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute element={
          <Layout>
            <AdminDashboardPage />
          </Layout>
        } allowedRoles={['SuperAdmin']} />
      } />
      <Route path="/admin/organizations" element={
        <ProtectedRoute element={
          <Layout>
            <OrganizationsPage />
          </Layout>
        } allowedRoles={['SuperAdmin']} />
      } />
      <Route path="/admin/organizations/:orgId" element={
        <ProtectedRoute element={
          <Layout>
            <OrganizationDetailsPage />
          </Layout>
        } allowedRoles={['SuperAdmin']} />
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute element={
          <Layout>
            <UsersPage />
          </Layout>
        } allowedRoles={['SuperAdmin']} />
      } />
      <Route path="/admin/quota" element={
        <ProtectedRoute element={
          <Layout>
            <QuotaPage />
          </Layout>
        } allowedRoles={['SuperAdmin']} />
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <ThemeProvider>
          <AppRoutes />
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;